import { useRef } from 'react'
import clamp from 'lodash-es/clamp'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import {
  EVENT_NAME_POINTER_CANCEL,
  EVENT_NAME_POINTER_MOVE,
  EVENT_NAME_POINTER_UP,
} from '../../constants/event'
import { preventDefault, stopPropagation } from '../../utils/events'

import {
  USE_WIDTH_RESIZE_INITIAL_STATE,
  USE_WIDTH_RESIZE_MAX_WIDTH,
  USE_WIDTH_RESIZE_MIN_WIDTH,
} from './useWidthResize.constants'
import {
  getResolvedMaxWidth,
  getResolvedMinWidth,
  getResolvedNextWidth,
} from './useWidthResize.utils'

export function useWidthResize({
  containerRef,
  disabled,
  getNextWidth,
  maxWidth = USE_WIDTH_RESIZE_MAX_WIDTH,
  maxWidthRatioToContainer,
  minWidth = USE_WIDTH_RESIZE_MIN_WIDTH,
  onResize,
  onResizeEnd,
  onResizeStart,
} = EMPTY_OBJECT) {
  const resizeStateRef = useRef(USE_WIDTH_RESIZE_INITIAL_STATE)

  const removeGlobalListenersRef = useRef(null)

  function cleanupPointerlListeners() {
    removeGlobalListenersRef.current?.()
    removeGlobalListenersRef.current = null
  }

  function stopResize(event) {
    resizeStateRef.current.pointerId = null
    resizeStateRef.current.isResizing = false

    document.body.style.userSelect = EMPTY_STRING
    document.body.style.cursor = EMPTY_STRING

    cleanupPointerlListeners()

    onResizeEnd?.({ event })
  }

  function handlePointerMove(event) {
    const { isResizing, pointerId, startWidth, startX } = resizeStateRef.current

    if (isResizing || pointerId === event.pointerId) {
      const deltaX = event.clientX - startX
      const width = getResolvedNextWidth({
        containerRef,
        currentX: event.clientX,
        getNextWidth,
        maxWidth,
        maxWidthRatioToContainer,
        minWidth,
        startWidth,
        startX,
      })

      onResize?.({
        deltaX,
        event,
        startWidth,
        startX,
        width,
      })
    }
  }

  function handlePointerUp(event) {
    const { isResizing, pointerId } = resizeStateRef.current

    if (isResizing || pointerId === event.pointerId) {
      stopResize(event)
    }
  }

  function handlePointerCancel(event) {
    const { isResizing, pointerId } = resizeStateRef.current

    if (isResizing || pointerId === event.pointerId) {
      stopResize(event)
    }
  }

  function addPointerListeners() {
    window.addEventListener(EVENT_NAME_POINTER_MOVE, handlePointerMove)
    window.addEventListener(EVENT_NAME_POINTER_UP, handlePointerUp)
    window.addEventListener(EVENT_NAME_POINTER_CANCEL, handlePointerCancel)

    removeGlobalListenersRef.current = () => {
      window.removeEventListener(EVENT_NAME_POINTER_MOVE, handlePointerMove)
      window.removeEventListener(EVENT_NAME_POINTER_UP, handlePointerUp)
      window.removeEventListener(EVENT_NAME_POINTER_CANCEL, handlePointerCancel)
    }
  }

  function handleResizeStart({ event, width = 0 }) {
    if (!disabled) {
      preventDefault(event)
      stopPropagation(event)

      cleanupPointerlListeners()

      const safeWidth = clamp(
        width,
        getResolvedMinWidth({ currentWidth: width, minWidth }),
        getResolvedMaxWidth({
          containerRef,
          currentWidth: width,
          maxWidth,
          maxWidthRatioToContainer,
        })
      )

      resizeStateRef.current = {
        pointerId: event.pointerId,
        startWidth: safeWidth,
        startX: event.clientX,
      }

      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'

      addPointerListeners()

      onResizeStart?.({
        event,
        width: safeWidth,
      })

      event.currentTarget.setPointerCapture?.(event.pointerId)
    }
  }

  function getWidthByDelta({ deltaX, startWidth }) {
    return getResolvedNextWidth({
      containerRef,
      currentX: deltaX,
      getNextWidth,
      maxWidth,
      maxWidthRatioToContainer,
      minWidth,
      startWidth,
      startX: 0,
    })
  }

  return {
    getResolvedMaxWidth,
    getWidthByDelta,
    handleResizeStart,
    resizeStateRef,
  }
}
